import type dayjs from 'dayjs';
import type {
  ReceiptIntakeItem,
  ExistingStockItem,
  Stock,
  StockIntakePayload,
  StockItemCreateData,
  StockWithStockItems,
} from './entity';
import type { Dayjs } from 'dayjs';
import { apiCall, GetStockAllowance } from '@shared/entity';
import { axiosInstance } from '@libs/axios/axios';

export const stockApi = {
  getItems: (stockId: string) => `/api/stock/items?stockId=${encodeURIComponent(stockId)}`,
  getItemById: (stockItemId: string) => `/item/${encodeURIComponent(stockItemId)}`,
  deleteItemById: (stockItemId: string) => `api/stock/item/${encodeURIComponent(stockItemId)}`,
  createOrUpdateItem: () => '/api/stock',
  analyzeReceipt: () => '/api/stock/analyze-receipt',
  getAll: () => '/api/stock',
  getAllowances: (params: { teamId: string; fromDate: Dayjs; toDate: Dayjs }) =>
    `/api/stock-allowance/${encodeURIComponent(params.teamId)}?fromDate=${encodeURIComponent(
      params.fromDate.toISOString(),
    )}&toDate=${encodeURIComponent(params.toDate.toISOString())}`,
};

export const getStocks = async (): Promise<Stock[]> =>
  apiCall(async () => await axiosInstance.get(stockApi.getAll()), 'api.stock.notFound');

export const getStockItems = async (stockId: string): Promise<StockWithStockItems[]> =>
  apiCall(
    async () => await axiosInstance.get(stockApi.getItems(stockId)),
    'api.stock.itemsNotFound',
  );

export const getStockAllowances = async (params: {
  teamId: string;
  fromDate: dayjs.Dayjs;
  toDate: dayjs.Dayjs;
}): Promise<GetStockAllowance[]> =>
  apiCall(
    async () => await axiosInstance.get(stockApi.getAllowances(params)),
    'api.stock.consumptionNotFound',
  );

export const postCreateNewStockItem = async (
  stockItem: StockItemCreateData,
): Promise<ExistingStockItem> =>
  apiCall(
    async () => await axiosInstance.post(stockApi.createOrUpdateItem(), stockItem),
    'api.stock.upsertError',
  );

export const deleteStockItem = async (stockItemId: string): Promise<void> =>
  apiCall(
    async () => await axiosInstance.delete(stockApi.deleteItemById(stockItemId)),
    'api.stock.deleteError',
  );

export const postAnalyzeReceipt = async (
  imageData: string,
  stockId?: string,
): Promise<ReceiptIntakeItem[]> =>
  apiCall(
    async () =>
      await axiosInstance.post(stockApi.analyzeReceipt(), {
        imageData,
        stockId,
      }),
    'api.stock.receiptAnalyzeError',
  );

export const postStockIntakeItem = async (payload: StockIntakePayload): Promise<unknown> =>
  apiCall(
    async () => await axiosInstance.post(stockApi.createOrUpdateItem(), payload),
    'api.stock.upsertError',
  );
